import React, { useState, useRef, useEffect } from 'react';
import { Form, Button, Alert, Card, Spinner, FloatingLabel } from 'react-bootstrap';
import { 
  FaPaperPlane, 
  FaUser, 
  FaEnvelope, 
  FaComment, 
  FaCheck, 
  FaPaperclip,
  FaTimes,
  FaHeadset,
  FaArrowLeft,
  FaTrash
} from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../../contexts/AuthContext';
import './ContactForm.css';

const ContactForm = ({ onSubmit, onCancel }) => {
  const { currentUser } = useAuth();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
    subject: 'General Inquiry',
    files: []
  });
  
  // Update form data when currentUser becomes available
  useEffect(() => {
    if (currentUser) {
      setFormData(prev => ({
        ...prev,
        name: currentUser.displayName || '',
        email: currentUser.email || ''
      }));
    }
  }, [currentUser]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [touched, setTouched] = useState({});
  const formRef = useRef(null);

  const subjects = [
    'General Inquiry',
    'Account Help',
    'Technical Support',
    'Billing',
    'Feedback',
    'Other'
  ];

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    
    if (name === 'fileUpload') {
      if (files && files.length > 0) {
        const newFiles = Array.from(files).map(file => ({
          name: file.name,
          size: file.size,
          type: file.type,
          file: file
        }));
        
        setFormData(prev => ({
          ...prev,
          files: [...prev.files, ...newFiles]
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  
  const handleRemoveFile = (index) => {
    setFormData(prev => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index)
    }));
  };

  const handleBlur = (field) => {
    setTouched(prev => ({
      ...prev,
      [field]: true
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Mark all fields as touched for validation
    const newTouched = {};
    Object.keys(formData).forEach(field => {
      newTouched[field] = true;
    });
    setTouched(newTouched);
    
    // Basic validation
    if (!formData.name || !formData.email || !formData.message) {
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Real API call would look like:
      /*
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message
        })
      });
      
      if (!response.ok) throw new Error('Failed to submit form');
      */
      
      setSubmitStatus('success');
      setFormData(prev => ({ ...prev, message: '' }));
      
      // Auto-close after success if needed
      setTimeout(() => {
        if (onSubmit) onSubmit();
      }, 2500);
      
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderSuccessState = () => (
    <motion.div 
      className="contact-success"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
    >
      <div className="success-icon">
        <FaCheck />
      </div>
      <h4 className="text-center mb-3">Message Sent Successfully!</h4>
      <p className="text-center text-muted mb-4">
        Thank you for contacting us. Our team will get back to you within 24 hours.
      </p>
      <Button 
        variant="primary" 
        className="w-100"
        onClick={onCancel}
      >
        <FaArrowLeft className="me-2" /> Back to Chat
      </Button>
    </motion.div>
  );

  const renderForm = () => (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="contact-form"
    >
      <div className="contact-header">
        <div className="contact-icon">
          <FaHeadset />
        </div>
        <h4>Contact Support</h4>
        <p>We're here to help! Send us a message and we'll respond as soon as possible.</p>
      </div>
      
      {submitStatus === 'error' && (
        <Alert variant="danger" className="fade-in">
          <FaTimes className="me-2" />
          Oops! Something went wrong. Please try again.
        </Alert>
      )}
      
      <Form onSubmit={handleSubmit} ref={formRef} className="contact-form">
        <FloatingLabel controlId="name" label="Your Name" className="mb-3">
          <Form.Control
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            onBlur={() => handleBlur('name')}
            placeholder="Your Name"
            isInvalid={touched.name && !formData.name}
            disabled={isSubmitting}
          />
          <Form.Control.Feedback type="invalid">
            Please enter your name
          </Form.Control.Feedback>
        </FloatingLabel>
        
        <FloatingLabel controlId="email" label="Email Address" className="mb-3">
          <Form.Control
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            onBlur={() => handleBlur('email')}
            placeholder="Email Address"
            isInvalid={touched.email && !/^\S+@\S+\.\S+$/.test(formData.email)}
            disabled={isSubmitting || !!currentUser?.email}
          />
          <Form.Control.Feedback type="invalid">
            Please enter a valid email address
          </Form.Control.Feedback>
        </FloatingLabel>
        
        <FloatingLabel controlId="subject" label="Subject" className="mb-3">
          <Form.Select
            name="subject"
            value={formData.subject}
            onChange={handleChange}
            disabled={isSubmitting}
          >
            {subjects.map((subject) => (
              <option key={subject} value={subject}>
                {subject}
              </option>
            ))}
          </Form.Select>
        </FloatingLabel>
        
        <FloatingLabel controlId="message" label="Your Message">
          <Form.Control
            as="textarea"
            name="message"
            value={formData.message}
            onChange={handleChange}
            onBlur={() => handleBlur('message')}
            placeholder="Your Message"
            style={{ minHeight: '80px' }}
            isInvalid={touched.message && !formData.message}
            disabled={isSubmitting}
            className="mb-2"
          />
          <Form.Control.Feedback type="invalid">
            Please enter your message
          </Form.Control.Feedback>
        </FloatingLabel>
        
        {/* File Upload Section */}
        <div className="file-upload-container">
          <label className="d-block mb-2">Attachments (Optional)</label>
          <label className="upload-button">
            <FaPaperclip className="me-1" />
            Choose Files
            <input
              type="file"
              name="fileUpload"
              className="d-none"
              onChange={handleChange}
              multiple
            />
          </label>
          <div className="file-types">Supports: JPG, PNG, PDF, DOC, DOCX (Max 5MB each)</div>
          
          {formData.files.length > 0 && (
            <div className="file-preview">
              {formData.files.map((file, index) => (
                <div key={index} className="file-item">
                  <FaPaperclip />
                  <span className="text-truncate" style={{ maxWidth: '150px' }}>
                    {file.name}
                  </span>
                  <small className="text-muted ms-2">
                    ({(file.size / 1024).toFixed(1)} KB)
                  </small>
                  <button
                    type="button"
                    className="btn btn-link p-0 ms-2"
                    onClick={() => handleRemoveFile(index)}
                    disabled={isSubmitting}
                  >
                    <FaTrash className="text-danger" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        
        <div className="form-actions">
          <div className="d-grid">
            <Button 
              variant="primary" 
              type="submit" 
              size="lg"
              disabled={isSubmitting || !formData.name || !formData.email || !formData.message}
              className="submit-btn"
            >
              {isSubmitting ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Sending...
                </>
              ) : (
                <>
                  <FaPaperPlane className="me-2" />
                  Send Message
                </>
              )}
            </Button>
            
            <Button 
              variant="outline-secondary" 
              onClick={onCancel}
              disabled={isSubmitting}
              className="mt-2"
            >
              <FaTimes className="me-1" /> Cancel
            </Button>
          </div>
        </div>
      </Form>
    </motion.div>
  );

  return (
    <div className="contact-form-card">
      <AnimatePresence mode="wait">
        {submitStatus === 'success' ? renderSuccessState() : renderForm()}
      </AnimatePresence>
    </div>
  );
};

export default ContactForm;
